import { useMemo, useRef } from "react";
import useQueriedRecords from "./useQueriedRecords";
import useFieldMap from "../../../hooks/useFieldMap";
import Constants from "../../../utils/Constants";
import { callFunctionIfExists } from "../../../utils/GeneralUtils";

/**
 * useRecordAggregations - Queries any number / percent fields and returns the aggregated values.
 *                         Allows for existing query.Aggregations object, or creates a new one.
 *                         Pass onSave function for new Aggregation object to be saved if none exists.
 *
 * @param {string} moduleAPIName - The api of name of the module you want to query.
 * @param {object} query - The query object to use for the query. See Docs: https://emerge360.atlassian.net/wiki/spaces/GC2/pages/483196929/GrowthCloud+CRM+Search+API+Syntax
 * @param {function=} onSave - Optional function to be called when the Aggregation object is saved.
 * @param {*} variableValues - Variable values to be used to generate dynamic values in the query.
 */
const useRecordAggregations = ({
  moduleAPIName,
  query,
  onSave,
  disabled,
  viewFilters,
  variableValues,
}: {
  moduleAPIName: string;
  query: any;
  onSave?: (q: any) => void;
  disabled?: boolean;
  viewFilters?: any;
  variableValues?: any;
}) => {
  const fieldMap = useFieldMap(moduleAPIName);
  const queryStringified = JSON.stringify(query);
  const queryRef = useRef<any>();

  queryRef.current = query;

  const isDisabled = !query || Object.keys(query).length === 0;

  const finalQuery = useMemo(() => {
    const query = queryRef.current;
    if (disabled || !query || Object.keys(query).length === 0) return;

    // If the query has saved aggregations, return them
    if (query?.Aggregations && query?.Aggregations.length > 0) {
      return { Aggregations: query?.Aggregations, Filters: query?.Filters };
    }

    const queryFields = query?.Fields;
    const aggregations = [];

    for (let i = 0; i < queryFields?.length; i++) {
      const field = queryFields[i];
      const fieldSchema = fieldMap[field];
      const fieldType = fieldSchema?.FieldType;

      // If the field is a number field, add it to the aggregations.
      if (
        Constants.NUMBER_TYPES.includes(fieldType) ||
        fieldSchema?.CalculatedFieldData?.Type === "number"
      ) {
        aggregations.push({
          Field: field,
          Method: "Sum",
        });
      }
    }

    if (JSON.stringify(query?.Aggregations) !== JSON.stringify(aggregations)) {
      callFunctionIfExists(onSave, { ...query, Aggregations: aggregations });
    }

    return { Aggregations: aggregations, Filters: query?.Filters };
  }, [queryStringified, onSave, fieldMap, disabled]);

  const { records, recordsLoading, error, errorMsg } = useQueriedRecords({
    moduleAPIName: moduleAPIName,
    query: finalQuery,
    disabled: isDisabled || disabled,
    viewFilters: viewFilters?.Filters,
    variableValues: variableValues,
  });

  return {
    aggregations: records ? records[0] : null,
    aggregationsLoading: recordsLoading,
    error,
    errorMsg,
  };
};

export default useRecordAggregations;
